import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface OwaspTop10PageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 14,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 16,
    color: '#475569',
    fontSize: 11.5,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 34,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  testedPill: {
    backgroundColor: '#E8F8F0',
    borderWidth: 1,
    borderColor: '#86EFAC',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
  },
  testedPillText: {
    color: '#15803D',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  notTestedPill: {
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
  },
  notTestedPillText: {
    color: '#6B7280',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  findingsPill: {
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#FCA5A5',
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
  },
  findingsPillText: {
    color: '#EF4444',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  cleanPill: {
    backgroundColor: '#1E293B',
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
  },
  cleanPillText: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  plainCleanText: {
    color: '#374151',
    fontSize: 8.5,
  },
});

const OwaspTop10PageV2: React.FC<OwaspTop10PageProps> = ({ reportData, sectionId }) => {
  const findings = reportData.detailed_findings || [];
  const targets = reportData.target_details || [];
  
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  if (isNetwork && webTargets.length === 0) {
    return null;
  }

  const defaultCategories = [
    { code: 'A01:2025', name: 'Broken Access Control', key: 'broken access control' },
    { code: 'A02:2025', name: 'Security Misconfiguration', key: 'security misconfiguration' },
    { code: 'A03:2025', name: 'Software Supply Chain Failures', key: 'software supply chain failures' },
    { code: 'A04:2025', name: 'Cryptographic Failures', key: 'cryptographic failures' },
    { code: 'A05:2025', name: 'Injection', key: 'injection' },
    { code: 'A06:2025', name: 'Insecure Design', key: 'insecure design' },
    { code: 'A07:2025', name: 'Authentication Failures', key: 'authentication' },
    { code: 'A08:2025', name: 'Software or Data Integrity Failures', key: 'integrity failures' },
    { code: 'A09:2025', name: 'Security Logging and Alerting Failures', key: 'logging and monitoring' },
    { code: 'A10:2025', name: 'Mishandling of Exceptional Conditions', key: 'mishandling of exceptional conditions' }
  ];

  const owaspCategories = reportData.finding_config?.owaspCustomCategories || defaultCategories;

  const getCategoryStatus = (categoryKey: string) => {
    const matched = findings.filter(f => {
      const owaspStr = (f.owasp_category || f.owasp || '').toLowerCase();
      return owaspStr.includes(categoryKey.toLowerCase());
    });

    if (matched.length > 0) {
      const ids = matched.map(m => m.report_id || m.id || '').filter(Boolean).join(', ');
      return { status: 'Findings', text: `Findings (${ids})`, isClean: false };
    }
    return { status: 'Clean', text: 'Clean', isClean: true };
  };

  const renderResultPill = (catCode: string, resText: string, isClean: boolean, isTested: boolean) => {
    if (!isTested) {
      return <Text style={styles.plainCleanText}>N/A</Text>;
    }
    if (!isClean) {
      return (
        <View style={styles.findingsPill}>
          <Text style={styles.findingsPillText}>{resText}</Text>
        </View>
      );
    }
    return (
      <View style={styles.cleanPill}>
        <Text style={styles.cleanPillText}>No issues</Text>
      </View>
    );
  };

  const headingText = reportData.finding_config?.owaspHeading || '4. OWASP Top 10 (2025) Coverage';
  const descriptionText = reportData.finding_config?.owaspDescription || 'Coverage of each OWASP Top 10 category during this engagement and whether issues were identified.';

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Title */}
      <Text style={styles.sectionTitle}>{headingText}</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      {/* Sub-text */}
      <Text style={styles.paragraph}>
        {descriptionText}
      </Text>

      {/* OWASP Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: '45%' }]}>Category</Text>
          <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>Tested</Text>
          <Text style={[styles.tableHeaderCell, { width: '40%' }]}>Result</Text>
        </View>
        {owaspCategories.map((cat: any, idx: number) => {
          const configCode = cat.code.includes(':') ? cat.code : `${cat.code}:2025`;
          const owaspCoverage = reportData.finding_config?.owaspCoverage || {};
          const settings = owaspCoverage[cat.code] || owaspCoverage[configCode] || { tested: true, resultOverride: '' };
          
          const isTested = settings.tested !== false;
          let resText = '';
          let isClean = true;

          if (!isTested) {
            resText = 'N/A';
            isClean = true;
          } else if (settings.resultOverride) {
            resText = settings.resultOverride;
            const lowerRes = resText.toLowerCase();
            isClean = lowerRes.includes('clean') || lowerRes.includes('no issues') || lowerRes.includes('n/a');
          } else {
            const res = getCategoryStatus(cat.key);
            resText = res.text;
            isClean = res.isClean;
          }

          const isLast = idx === owaspCategories.length - 1;

          return (
            <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
              {/* Category joined as AXX:YEAR - Name */}
              <Text style={[styles.tableRowCell, { width: '45%', color: '#1F2937' }]}>
                {cat.code} - {cat.name}
              </Text>
              
              {/* Tested pill */}
              <View style={[styles.tableRowCell, { width: '15%', alignItems: 'center' }]}>
                <View style={isTested ? styles.testedPill : styles.notTestedPill}>
                  <Text style={isTested ? styles.testedPillText : styles.notTestedPillText}>
                    {isTested ? 'Yes' : 'No'}
                  </Text>
                </View>
              </View>

              {/* Result Pill */}
              <View style={[styles.tableRowCell, { width: '40%', justifyContent: 'center' }]}>
                {renderResultPill(cat.code, resText, isClean, isTested)}
              </View>
            </View>
          );
        })}
      </View>
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default OwaspTop10PageV2;
